import { useColorScheme } from '@mui/material/styles';
import { useEffect } from 'react';

export const THEME_MODE_STORAGE_KEY = 'themeMode';

/**
 * The theme style the user has chosen to use. (includes system)
 */
export type ThemeStyle = 'light' | 'dark' | 'system' | 'compatibility';

/**
 * The theme mode we will render (system will be converted into "light" or "dark")
 */
export type ThemeMode = 'light' | 'dark' | 'compatibility';
export type ColorVariant = 'dark' | 'light';

/**
 * Type for the primary palette, used to access the selected state color.
 */
export type PalettePrimaryType = {
    _states?: {
        selected?: string;
        hover?: string;
    };
};

/**
 * Used by WDSThemeProvider, this simply applies the given theme using the MUI hook.
 *
 * It is a faux component and does not render anything. It needs to be this way as it has to exist inside the WDSThemeProvider
 * @param param0
 * @returns
 */
export function ThemeSetter({ mode }: { mode: 'light' | 'dark' }) {
    const { setMode } = useColorScheme();

    // Only react to the mode *prop* changing — not to MUI's internal mode.
    // MUI has its own cross-tab sync via the 'mui-mode' storage key.
    // If we also depended on MUI's currentMode here, a cross-tab storage
    // event would temporarily make currentMode !== mode (before WDS state
    // updates), causing ThemeSetter to call setMode() with the old value
    // and producing a visible flicker.
    useEffect(() => {
        setMode(mode);
    }, [mode, setMode]);

    return <span />;
}

export function getThemeStyleLocalStorage(): ThemeStyle | undefined {
    const val = localStorage.getItem(THEME_MODE_STORAGE_KEY);
    if (val === 'light' || val === 'dark') {
        return val;
    }
    return undefined;
}

export function setThemeStyleLocalStorage(mode: ThemeStyle) {
    return localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
}

export function getSystemThemeMode(): 'light' | 'dark' {
    if (typeof window === 'undefined') {
        // If there is no window, we must be Server-side rendering, so we default to light
        return 'light';
    }
    const darkThemeSystem = window.matchMedia('(prefers-color-scheme: dark)');
    if (darkThemeSystem.matches) {
        return 'dark';
    }
    return 'light';
}
